/**
 * P3-14: JobButton —— 职业分配行（迁移自 js/village.js:3829-3993）。
 *
 * 旧实现要点：
 * - `JobButtonController.getName`：label + " (" + job.value + ")"；
 * - `JobButtonController.updateEnabled`：基类 hasResources（JobButton 无 prices，恒真）
 *   + `hasFreeKittens()` + `getJobLimit(job) > getWorkerKittens(job)`；
 * - `JobButtonController.clickHandler`：无修饰 assign 1、Shift assignAll（确认框）、
 *   Ctrl/Cmd batchSize；
 * - links：`[+]` / `[+5]` / `[+25]` / `[+100]` / `[btn.all.assign]` 与对应的
 *   `[–]` 组，仅当 `getKittens() >= amt*2` 且 `getJobLimit(job) >= amt` 时生成
 *   扩展档；两组 links 均用 `addLinkList` 渲染为首项显示 + 余项 hover dropdown；
 * - tooltip：`ButtonModernHelper.getTooltipHTML(controller, {options})`，内容含
 *   desc / effectModels（由 job.modifiers 转换）/ flavor。
 *
 * 本组件通过 `useButtonModel` 每 tick 快照 `controller.fetchModel(opts)`；links
 * 的可见性/文案变化会触发重渲染，点击 handler 取 `modelRef.current` 最新闭包
 *（与旧版 this.model 语义一致）。
 */
import { useCallback, useMemo, useState } from "react";
import type { MouseEvent, ReactNode } from "react";
import { getGame } from "../../bridge/GameBridge";
import type { GamePage } from "../../bridge/GameBridge";
import { GameButton } from "../../components/GameButton";
import type { GameButtonLinkModel } from "../../components/GameButton";
import { makeModernTooltip, useButtonModel } from "../bonfire/useButtonModel";
import type { ModernButtonController } from "../bonfire/useButtonModel";

// ---------------------------------------------------------------------------
// 类型
// ---------------------------------------------------------------------------

interface JobButtonLinkSnap {
  id: string;
  title: string;
}

interface JobButtonSnapshot {
  name: string;
  description: string;
  visible: boolean;
  enabled: boolean;
  resourceIsLimited: boolean | string;
  /** 当前职业分配的猫数（model.job.value） */
  value: number;
  /** 减档 links 的标题序列（用于驱动重渲染；handler 从 modelRef 取最新） */
  unassignLinks: JobButtonLinkSnap[];
  /** 加档 links 的标题序列 */
  assignLinks: JobButtonLinkSnap[];
}

interface LinkListItem {
  id: string;
  title: string;
  handler: () => void;
}

// ---------------------------------------------------------------------------
// 快照
// ---------------------------------------------------------------------------

function toJobSnapshot(model: Record<string, unknown>): JobButtonSnapshot {
  const job = (model.job ?? {}) as { value?: number };
  const unassignLinks = (model.unassignLinks as { id?: string; title: string }[]) ?? [];
  const assignLinks = (model.assignLinks as { id?: string; title: string }[]) ?? [];
  return {
    name: model.name as string,
    description: (model.description as string) ?? "",
    visible: model.visible !== false,
    enabled: model.enabled !== false,
    resourceIsLimited: (model.resourceIsLimited as boolean | string) ?? "",
    value: job.value ?? 0,
    unassignLinks: unassignLinks.map((l) => ({ id: l.id ?? l.title, title: l.title })),
    assignLinks: assignLinks.map((l) => ({ id: l.id ?? l.title, title: l.title })),
  };
}

function jobSnapshotEq(a: JobButtonSnapshot | null, b: JobButtonSnapshot | null): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  const linksEq = (la: JobButtonLinkSnap[], lb: JobButtonLinkSnap[]) =>
    la.length === lb.length && la.every((v, i) => v.id === lb[i].id && v.title === lb[i].title);
  return (
    a.name === b.name &&
    a.description === b.description &&
    a.visible === b.visible &&
    a.enabled === b.enabled &&
    a.resourceIsLimited === b.resourceIsLimited &&
    a.value === b.value &&
    linksEq(a.unassignLinks, b.unassignLinks) &&
    linksEq(a.assignLinks, b.assignLinks)
  );
}

// ---------------------------------------------------------------------------
// addLinkList 等价 dropdown（core.js:1132-1215）
// ---------------------------------------------------------------------------

/**
 * 旧 `ButtonModern.addLinkList` 的 React 等价：div(float:right) > 首项根链接 +
 * div.linkContent（hover 显示）> 剩余 dropdown-link。根链接点击执行首项 handler。
 */
function LinkListDropdown({ items }: { items: LinkListItem[] }) {
  const [open, setOpen] = useState(false);
  if (!items.length) {
    return null;
  }
  const [root, ...rest] = items;
  const onLinkClick = (handler: () => void) => (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    handler();
  };
  return (
    <div
      style={{ float: "right" }}
      onMouseOver={() => setOpen(true)}
      onMouseOut={() => setOpen(false)}
    >
      <a
        href="#"
        className={`${root.id}Link`}
        style={{ display: "block", float: "right" }}
        title={root.title}
        onClick={onLinkClick(root.handler)}
        dangerouslySetInnerHTML={{ __html: root.title }}
      />
      <div
        className="linkContent"
        style={{
          display: open ? "block" : "none",
          position: "absolute",
          float: "right",
          marginTop: "35px",
          zIndex: 100,
        }}
      >
        {rest.map((item) => (
          <a
            key={item.id}
            href="#"
            className="dropdown-link"
            style={{ display: "block" }}
            title={item.title}
            onClick={onLinkClick(item.handler)}
            dangerouslySetInnerHTML={{ __html: item.title }}
          />
        ))}
      </div>
    </div>
  );
}

declare global {
  interface Window {
    com?: {
      nuclearunicorn?: {
        game?: {
          ui?: {
            JobButtonController?: new (game: GamePage) => ModernButtonController;
          };
        };
      };
    };
  }
}

export interface JobButtonProps {
  /** 职业 name（game.village.jobs 中的键，如 "woodcutter"） */
  jobName: string;
}

export function JobButton({ jobName }: JobButtonProps) {
  const controller = useMemo(() => {
    const game = getGame();
    const Ctor = window.com?.nuclearunicorn?.game?.ui?.JobButtonController;
    if (!game || !Ctor) {
      return null;
    }
    return new Ctor(game);
  }, []);

  const createOpts = useCallback(
    (game: GamePage) => {
      const job = game.village.getJob(jobName);
      return {
        name: job.title,
        job: jobName,
      };
    },
    [jobName],
  );

  const { snap, modelRef } = useButtonModel(controller, createOpts, toJobSnapshot, jobSnapshotEq);

  // 无修饰 +1、Shift +all、Ctrl/Cmd +batchSize；旧 controller.clickHandler 内部处理
  const onClick = useCallback(
    (event: MouseEvent) => {
      controller?.clickHandler?.(modelRef.current, event);
    },
    [controller, modelRef],
  );

  const getTooltip = useMemo(
    () => makeModernTooltip(() => controller, createOpts),
    [controller, createOpts],
  );

  const getSelectedObject = useCallback(() => modelRef.current, [modelRef]);

  if (!snap) {
    return null;
  }

  // handler 闭包：事件触发时从 modelRef 取最新 handler（符合 React ref 读取规则）
  const makeLinkHandler = (isAssign: boolean, index: number) => () => {
    const model = modelRef.current;
    if (!model) {
      return;
    }
    const links = isAssign
      ? (model.assignLinks as GameButtonLinkModel[] | undefined)
      : (model.unassignLinks as GameButtonLinkModel[] | undefined);
    const handler = links?.[index]?.handler as (() => void) | undefined;
    handler?.();
  };

  const children: ReactNode = (
    <>
      {snap.unassignLinks.length > 0 && (
        <LinkListDropdown
          items={snap.unassignLinks.map((link, i) => ({
            id: link.id,
            title: link.title,
            handler: makeLinkHandler(false, i),
          }))}
        />
      )}
      {snap.assignLinks.length > 0 && (
        <LinkListDropdown
          items={snap.assignLinks.map((link, i) => ({
            id: link.id,
            title: link.title,
            handler: makeLinkHandler(true, i),
          }))}
        />
      )}
    </>
  );

  return (
    <GameButton
      name={snap.name}
      description={snap.description}
      visible={snap.visible}
      enabled={snap.enabled}
      resourceIsLimited={snap.resourceIsLimited}
      getTooltip={getTooltip}
      onClick={onClick}
      getSelectedObject={getSelectedObject}
    >
      {children}
    </GameButton>
  );
}
